import { Button, Typography, Grid, Dialog, DialogContent, useMediaQuery, Box } from "@mui/material";
import { Help as HelpIcon } from "@mui/icons-material";
import RoundBackIcon from "../../../icons/RoundBackIcon.svg";
import { MaskedInputState, StyledMaskedInput } from "../../../shared/MaskedInput";
import React from "react";
import { format, parseISO } from "date-fns";
import NumberFormat from "react-number-format";
import { getSumOfPropPayTypesAmount } from "./UseSharedMethods";
import { BalanceDetailsDTO } from "@inosio/payments-sdk";
import { useSelector } from "react-redux";
import { selectPropertyGroupDetailsState } from "../../../shared/sagas/propertyGroups/propertyGroupsSlice";

interface PaymentBalanceDetailsPropType {
  balanceInfoContext: BalanceDetailsDTO;//this has moved from "what is it right now" to what is it "for when this payment should process"
  maskedInputValues: MaskedInputState;
  open: boolean;
  closeCallback: () => void;
  continueCallback: (value: MaskedInputState) => void;
}

export default function PaymentBalanceDetails(props: PaymentBalanceDetailsPropType) {
  const { continueCallback, closeCallback, open, balanceInfoContext: currentBalanceInfo } = props;
  const isOnMobileScreen = useMediaQuery("(max-width:600px)");
  const [maskedInputValues, setMaskedInputValues] = React.useState<MaskedInputState>(props.maskedInputValues);
  const { propertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);


  React.useEffect(() => {
    setMaskedInputValues(props.maskedInputValues);
  }, [props.maskedInputValues])
  
  const handleMaskedInputChange = (event: React.ChangeEvent<HTMLInputElement>) => setMaskedInputValues({ ...maskedInputValues, [event.target.name]: event.target.value });
  
  const [summationError, setSummationError] = React.useState('');
  // get sum of all the values for each property payment type
  const checkSummationErrors = () => {
    const sum = getSumOfPropPayTypesAmount(maskedInputValues);
    const max = currentBalanceInfo?.MaxPayment;
    const min = currentBalanceInfo?.MinPayment;
    if (sum <= max && sum >= min) {
      setSummationError("");
    } else {
      setSummationError("The total amount must be between the minimum and maximum amount.");
    }
  };

  const handleContinue = () => {
    continueCallback(maskedInputValues);
  }

  const amountOwedPaymentField = currentBalanceInfo?.PropertyPaymentTypes?.find(ppt=>ppt.Label==='Amount Owed');
  const secDepPaymentField = currentBalanceInfo?.PropertyPaymentTypes?.find(ppt=>ppt.Label==='Security Deposit');
  const miscPaymentField = currentBalanceInfo?.PropertyPaymentTypes?.find(ppt=>ppt.Label==='Misc');

  const isOTPEnabled = currentBalanceInfo?.PaymentMethodAllowed && propertyGroupDetails.PaymentsEnabled && !!currentBalanceInfo.PropertyPaymentTypes?.length;

  return (
    <Dialog
      open={open}
      onClose={closeCallback}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      fullScreen={isOnMobileScreen}
      fullWidth={true}
      disableEnforceFocus
      hideBackdrop={true}
      PaperProps={{
        style: {
          pointerEvents: "auto",
          height: !isOnMobileScreen ? "700px" : "100%",
          borderRadius: "24px",
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            height: isOnMobileScreen ? "" : "660px",
            padding: "5px 7px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <img src={RoundBackIcon} alt="back" style={{ cursor: "pointer", width: 26, height: 26 }} onClick={closeCallback} />
            </Grid>
            <Grid item xs={4}>
              <Typography id="payment-method-title">Details</Typography>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: "right" }}>
                <Button style={{ width: "100px" }} variant="contained" id="add-bank-btn" disabled={!!summationError.length || !isOTPEnabled} onClick={handleContinue}>
                  Continue
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Typography
                style={{
                  fontFamily: "Work Sans",
                  fontSize: "16px",
                  lineHeight: "36px",
                  color: "#000",
                }}
              >
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(currentBalanceInfo.Balance ?? 0) / 100}
                  prefix="Current Balance: $"
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} lg={6} sx={{ display: "flex", justifyContent: "end" }}>
              <Typography
                style={{
                  fontFamily: "Work Sans",
                  fontSize: "12px",
                  lineHeight: "36px",
                  color: "#000",
                }}
              >
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(currentBalanceInfo.MinPayment ?? 0) / 100}
                  prefix="Min: $"
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Typography>
              <Typography
                style={{
                  fontFamily: "Work Sans",
                  fontSize: "12px",
                  lineHeight: "36px",
                  color: "#000",
                  marginLeft: "10px",
                }}
              >
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(currentBalanceInfo.MaxPayment ?? 0) / 100}
                  prefix="Max: $"
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Typography>
            </Grid>
            <Grid container item xs={12}>
              {currentBalanceInfo?.PropertyPaymentTypes?.length === 1 ? (
                <React.Fragment>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      style={{
                        fontFamily: "Work Sans",
                        fontSize: "16px",
                        lineHeight: "36px",
                        color: "#000",
                        marginRight: "22px",
                      }}
                    >
                      Enter the amount you’d like to pay:
                    </Typography>
                    <StyledMaskedInput
                      id="owed"
                      disabled={currentBalanceInfo?.MinPayment === currentBalanceInfo?.MaxPayment}
                      error={!!summationError}
                      onBlur={(e: any) => checkSummationErrors()}
                      label=""
                      name={!!amountOwedPaymentField ? 'amountOwed' : !!secDepPaymentField ? 'securityDeposit' : !!miscPaymentField ? 'misc' : ''}
                      values={maskedInputValues}
                      handleChange={handleMaskedInputChange}
                    />
                  </Box>
                  {summationError?.length ? (
                    <Typography
                      style={{
                        fontFamily: "Work Sans",
                        fontStyle: "normal",
                        fontSize: "12px",
                        lineHeight: "14px",
                        color: "#DB3939",
                        marginTop: "8px",
                      }}
                    >
                      {summationError}
                    </Typography>
                  ) : (
                    <></>
                  )}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {amountOwedPaymentField && (
                    <Grid item xs={6} sm={4} md={4}>
                      <Typography
                        style={{
                          fontFamily: "Work Sans",
                          fontSize: "14px",
                          lineHeight: "36px",
                          color: "#000",
                        }}
                      >
                        {'Amount Owed'}
                        {""}
                        <HelpIcon
                          sx={{
                            fontSize: 17,
                            color: "#C4C4C4",
                            cursor: "pointer",
                          }}
                        />
                      </Typography>
                      <StyledMaskedInput 
                        error={!!summationError}
                        onBlur={(e: any) => checkSummationErrors()} 
                        label="" 
                        name="amountOwed" 
                        values={maskedInputValues} 
                        handleChange={handleMaskedInputChange} 
                      />
                    </Grid>
                  )}
                  {secDepPaymentField && (
                    <Grid item xs={6} sm={4} md={4} sx={{ textAlign: "center" }}>
                      <Typography
                        style={{
                          fontFamily: "Work Sans",
                          fontSize: "14px",
                          lineHeight: "36px",
                          color: "#000",
                        }}
                      >
                        {'Security Deposit'}
                        {""}
                        <HelpIcon
                          sx={{
                            fontSize: 17,
                            color: "#C4C4C4",
                            cursor: "pointer",
                          }}
                        />
                      </Typography>
                      <StyledMaskedInput 
                        error={!!summationError}
                        onBlur={(e: any) => checkSummationErrors()} 
                        label="" 
                        name="securityDeposit" 
                        values={maskedInputValues} 
                        handleChange={handleMaskedInputChange} 
                      />
                    </Grid>
                  )}
                  {miscPaymentField && (
                    <Grid item xs={6} sm={4} md={4}>
                      <Typography
                        style={{
                          fontFamily: "Work Sans",
                          fontSize: "14px",
                          lineHeight: "36px",
                          color: "#000",
                        }}
                      >
                        {'Misc'}
                        {""}
                        <HelpIcon
                          sx={{
                            fontSize: 17,
                            color: "#C4C4C4",
                            cursor: "pointer",
                          }}
                        />
                      </Typography>
                      <StyledMaskedInput 
                        error={!!summationError}
                        onBlur={(e: any) => checkSummationErrors()} 
                        label="" 
                        name="misc" 
                        values={maskedInputValues} 
                        handleChange={handleMaskedInputChange} 
                      />
                    </Grid>
                  )}

                  {summationError?.length ? (
                    <Typography
                      style={{
                        fontFamily: "Work Sans",
                        fontStyle: "normal",
                        fontSize: "12px",
                        lineHeight: "14px",
                        color: "#DB3939",
                        marginTop: "8px",
                      }}
                    >
                      {summationError}
                    </Typography>
                  ) : (
                    <></>
                  )}
                </React.Fragment>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography
                style={{
                  fontFamily: "Work Sans",
                  fontSize: "16px",
                  lineHeight: "36px",
                  color: "#000",
                }}
              >
                Charge Details
              </Typography>
              <div style={{ borderBottom: "1px solid #F1F0F5", margin: "5px 12px 0px" }}></div>
            </Grid>
          </Grid>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <Box sx={{ flex: 1 }}>
              {currentBalanceInfo?.ChargeDetails?.length > 0 ? (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{
                    paddingTop: "0px !important",
                    maxHeight: currentBalanceInfo?.PropertyPaymentTypes?.length === 1 ? 370 : 410,
                    overflow: "auto",
                    "&::-webkit-scrollbar": {
                      width: 7,
                    },
                    "&::-webkit-scrollbar-track": {
                      boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                      borderRadius: "24px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "darkgrey",
                      borderRadius: "24px",
                    },
                  }}
                >
                  {currentBalanceInfo?.ChargeDetails?.slice(0, 21)?.map((detail, index) => (
                    <Grid container item xs={12} key={index} sx={{ borderBottom: "1px solid #F1F0F5", margin: "0 12px", padding: "3px 17px" }}>
                      <Grid item xs={12} md={6} lg={6}>
                        <Typography
                          style={{
                            fontFamily: "Work Sans",
                            fontSize: "14px",
                            lineHeight: "36px",
                            color: "#000",
                          }}
                        >
                          {detail?.ChargeDescription}: ${(detail?.ChargeAmount / 100).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6} lg={6}>
                        {!!detail?.ChargeDueDate && (
                          <Typography
                            style={{
                              fontFamily: "Work Sans",
                              fontSize: "14px",
                              lineHeight: "36px",
                              color: "#000",
                            }}
                          >
                            Due: {format(parseISO(detail?.ChargeDueDate), "P")}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography
                  style={{
                    fontFamily: "Work Sans",
                    fontSize: "14px",
                    lineHeight: "36px",
                    color: "#000",
                    marginTop: "45px",
                    textAlign: "center",
                  }}
                >
                  No charge details available.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
